/**
 * MAPA (10.5Q) — tela cheia, ARRASTÁVEL e com zoom. Mostra os territórios do mundo com nome,
 * tier, gangue e os pontos de interesse (base, altar, portal, covil do boss, baús) além da
 * posição do Caçador. A viagem entre regiões virou uma fileira de abas no topo (a lista de
 * cartões não fazia sentido dentro de um mapa). Tocar num território mostra quem domina o
 * pedaço; arrastar move o mapa; ⊕/⊖ e pinça dão zoom; ⌖ recentraliza no Caçador.
 */
import { DIVIDE_MAP_COLOR, type DivideType } from "../world/Divides";
import { i18n } from "../i18n/i18n";

/** 10.9HP — ícone por objetivo de área */
const GOAL_ICON: Record<string, string> = { territories: "🗺", champions: "👑", kills: "⚔", chests: "📦", boss: "💀" };

export interface WorldRow {
  id: string;
  name: string;
  tier: number;
  unlocked: boolean;
  current: boolean;
  completed: boolean;
  recommendedLevel: number;
  levelOk?: boolean; // 10.9FE — ja tem o nivel: o que falta sao as MISSOES da area anterior
  goals?: { kind: string; progress: number; target: number }[]; // 10.9HP
  masteryLevel?: number;
  masteryMax?: number;
  surgeMaxTier?: number;
  surgeActiveTier?: number;
}

export interface MapTerritory {
  id: string;
  name: string;
  cx: number;
  cz: number;
  halfW: number;
  halfH: number;
  tier: number;
  current: boolean;
  gang: string[];
  champion?: string;
  discovered: boolean; // 10.5Q: só pedaços já visitados mostram detalhe
  visited?: boolean; // 10.9L6 — já contou pra "explorar territórios" NESTA volta
  ground?: string; // 10.9HL — cor do piso do bioma (css)
  /** 10.9HP — missão do território (abre as portas dele) e estado */
  tasks?: { kind: string; progress: number; target: number }[];
  cleared?: boolean;
  open?: boolean;
  needLevel?: number;
  col: number; // 10.7I — posição na grade (p/ desenhar trilhas entre vizinhos)
  row: number;
}
export interface MapPoi {
  x: number;
  z: number;
  kind: "sanctuary" | "altar" | "rift" | "boss" | "chest" | "enemy" | "elite";
}
export interface WorldMapData {
  regionName: string;
  playerLevel?: number; // 10.9HP
  goals?: { kind: string; progress: number; target: number }[]; // 10.9HP — objetivos da área
  nextRegion?: { name: string; level: number }; // 10.9HP — o que o Portal Rift abre
  goalBoss?: string; // 10.9L3 — qual boss conta no objetivo "boss" desta área (voidAlpha/aetherColossus/guardian)
  nextDepth?: number; // 10.9L4 — última área: concluir fecha a volta (Profundidade seguinte)
  /** 10.9L4 — onde está o boss que conta nesta área (Guardião: `ready` = já acordou; have/need = abates). */
  bossTarget?: { x: number; z: number; ready: boolean; name: string; have?: number; need?: number; lock?: string };
  half: number;
  territories: MapTerritory[];
  player: { x: number; z: number; facing: number };
  sanctuary: { x: number; z: number; r: number };
  pois: MapPoi[];
  /** 10.9EL — portas entre territorios (a/b = ids dos dois lados). */
  gates?: { x: number; z: number; alongX: boolean; half: number; open: boolean; a: string; b: string }[];
  /** 10.9HN — divisas naturais (rio/lava/gelo/abismo) entre territórios. */
  divides?: { x0: number; z0: number; x1: number; z1: number; type: string; a: string; b: string }[];
  /** 10.9CG — há um boss ativo (o mapa oferece "fugir da luta"). */
  bossActive?: boolean;
}

export interface WorldScreenHandlers {
  onTravel: (regionId: string) => void;
  onSurge: (regionId: string) => void;
  onMastery: (regionId: string) => void;
  onClose: () => void;
  /** 10.9CG — desistir da luta atual e voltar ao Santuário (Thiago: "impedido de sair da zona do boss"). */
  onFlee?: () => void;
}

const TIER_COLOR = ["#5ea86a", "#5ea86a", "#c8a94a", "#e08a4a", "#e05a6a", "#b25ae0"];

export class WorldScreen {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly infoEl: HTMLElement;
  private readonly tabsEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private provider: (() => WorldRow[]) | null = null;
  private mapProvider: (() => WorldMapData) | null = null;
  private map: WorldMapData | null = null;
  private selected: string | null = null;
  /** 10.9L4 — missão em FOCO (tocou na faixa de missões): o mapa acende onde fazer ela. */
  private focus: string | null = null;

  // câmera do mapa (mundo → tela)
  private scale = 2;
  private offX = 0; // centro do mapa (mundo) mostrado no centro do canvas
  private offZ = 0;
  private minScale = 1;
  private maxScale = 8;
  // gesto
  private dragging = false;
  private moved = 0;
  private lastX = 0;
  private lastY = 0;
  private pinchDist = 0;
  private readonly pointers = new Map<number, { x: number; y: number }>();

  constructor(uiRoot: HTMLElement, private readonly handlers: WorldScreenHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay map-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="map-full">
        <div class="map-top">
          <h2 class="map-title"></h2>
          <button class="btn map-bosses-btn" data-a="bosses">👑</button>
          <button class="btn map-x" data-a="close">✕</button>
        </div>
        <div class="map-bosses" hidden></div>
        <div class="map-tabs"></div>
        <div class="map-goals"></div>
        <div class="map-wrap">
          <canvas class="map-canvas"></canvas>
          <div class="map-tools">
            <button class="map-tool" data-a="zin">＋</button>
            <button class="map-tool" data-a="zout">－</button>
            <button class="map-tool" data-a="center">⌖</button>
          </div>
        </div>
        <div class="map-info"></div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.titleEl = this.root.querySelector(".map-title")!;
    this.tabsEl = this.root.querySelector(".map-tabs")!;
    this.canvas = this.root.querySelector(".map-canvas")!;
    this.infoEl = this.root.querySelector(".map-info")!;
    this.ctx = this.canvas.getContext("2d")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.handlers.onClose());
    this.root.querySelector('[data-a="bosses"]')!.addEventListener("click", () => this.toggleBosses()); // 10.9FY
    this.root.querySelector('[data-a="zin"]')!.addEventListener("click", () => this.zoom(1.35));
    this.root.querySelector('[data-a="zout"]')!.addEventListener("click", () => this.zoom(1 / 1.35));
    this.root.querySelector('[data-a="center"]')!.addEventListener("click", () => this.centerOnPlayer());
    this.bindGestures();
    i18n.onChange(() => { if (!this.root.hidden) this.renderAll(); });
  }

  open(provider: () => WorldRow[], mapProvider?: () => WorldMapData): void {
    this.provider = provider;
    this.mapProvider = mapProvider ?? null;
    this.selected = null;
    this.focus = null; // 10.9L4
    this.root.hidden = false;
    void this.root.offsetWidth; // força layout antes de medir o canvas
    this.map = this.mapProvider ? this.mapProvider() : null;
    this.fit(); // 10.9L4 — abre mostrando a grade INTEIRA da área (⌖ ainda centraliza no Caçador)
    this.renderAll();
    requestAnimationFrame(() => this.renderAll());
  }
  close(): void {
    this.root.hidden = true;
  }
  refresh(): void {
    if (!this.root.hidden) {
      this.map = this.mapProvider ? this.mapProvider() : this.map;
      this.renderAll();
    }
  }

  // ------------------------------------------------------------- câmera
  /** 10.9L4 — caixa que envolve os territórios (o mapa enquadra a GRADE, não a arena inteira —
   *  antes sobrava uma faixa vazia em cima/embaixo). */
  private bbox(): { x0: number; x1: number; z0: number; z1: number } | null {
    const ts = this.map?.territories ?? [];
    if (!ts.length) return null;
    return {
      x0: Math.min(...ts.map((t) => t.cx - t.halfW)), x1: Math.max(...ts.map((t) => t.cx + t.halfW)),
      z0: Math.min(...ts.map((t) => t.cz - t.halfH)), z1: Math.max(...ts.map((t) => t.cz + t.halfH)),
    };
  }
  private fit(): void {
    const m = this.map;
    const { w, h } = this.size();
    if (!m || w === 0) return;
    const b = this.bbox();
    if (!b) {
      this.minScale = Math.min(w, h) / (m.half * 2.1);
      this.maxScale = this.minScale * 7;
      this.scale = this.minScale * 2.1;
      return;
    }
    // a grade inteira cabe na tela (com 4% de folga); dá pra aproximar até 7×
    // 10.9L9 — descontando a FAIXA DE BAIXO (botões ＋－⌖ + legenda): antes eles ficavam em cima
    // dos territórios do canto, e a grade centralizada deixava uma faixa vazia em cima E embaixo
    const hAvail = Math.max(h * 0.5, h - this.bottomReserve(w));
    this.minScale = Math.min(w / ((b.x1 - b.x0) * 1.04), hAvail / ((b.z1 - b.z0) * 1.04));
    this.maxScale = this.minScale * 7;
    this.scale = this.minScale;
    this.offX = (b.x0 + b.x1) / 2;
    this.offZ = this.topAlignedZ(b.z0, h);
    this.fitW = w; this.fitH = h;
  }
  /** 10.9L9 — tamanho do mapa no último enquadramento (o painel de baixo muda de altura e encolhe o mapa) */
  private fitW = 0;
  private fitH = 0;
  /** 10.9L9 — altura (px) reservada embaixo pros botões em fileira + legenda das portas. A legenda
   *  (~190 px) + os 3 botões (146 px) pedem ~350 px lado a lado; mais estreito, a legenda sobe 1 linha. */
  private bottomReserve(w: number): number {
    return this.legendAbove(w) ? 86 : 58;
  }
  private legendAbove(w: number): boolean {
    return w < 350;
  }
  /** 10.9L9 — offZ que põe o TOPO da grade a 8 px do alto da tela (a sobra vai toda pra baixo). */
  private topAlignedZ(z0: number, h: number): number {
    return z0 + (h / 2 - 8) / this.scale;
  }
  private centerOnPlayer(): void {
    const m = this.map;
    if (!m) return;
    this.offX = m.player.x;
    this.offZ = m.player.z;
    this.clampCam();
    this.draw();
  }
  private zoom(f: number): void {
    this.scale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * f));
    this.clampCam();
    this.draw();
  }
  private clampCam(): void {
    const m = this.map;
    if (!m) return;
    const { w, h } = this.size();
    const halfViewX = w / 2 / this.scale;
    const halfViewZ = h / 2 / this.scale;
    const b = this.bbox();
    if (b) {
      // 10.9L4 — presa à GRADE: quando a vista é maior que a grade, fica centralizada nela
      const pad = 10;
      const clamp1 = (v: number, lo: number, hi: number, half: number): number =>
        hi - lo <= half * 2 ? (lo + hi) / 2 : Math.max(lo + half, Math.min(hi - half, v));
      this.offX = clamp1(this.offX, b.x0 - pad, b.x1 + pad, halfViewX);
      // 10.9L9 — no Z: se a grade cabe inteira, fica ENCOSTADA no alto (não centralizada); aproximado,
      // dá pra arrastar a última fileira pra cima da faixa dos botões
      const reserveZ = this.bottomReserve(w) / this.scale;
      const zLo = b.z0 - pad, zHi = b.z1 + pad + reserveZ;
      this.offZ = zHi - zLo <= halfViewZ * 2 ? this.topAlignedZ(b.z0, h) : Math.max(zLo + halfViewZ, Math.min(zHi - halfViewZ, this.offZ));
      return;
    }
    const lim = m.half + 8;
    const maxX = Math.max(0, lim - halfViewX);
    const maxZ = Math.max(0, lim - halfViewZ);
    this.offX = Math.max(-maxX, Math.min(maxX, this.offX));
    this.offZ = Math.max(-maxZ, Math.min(maxZ, this.offZ));
  }
  private size(): { w: number; h: number } {
    const wrap = this.canvas.parentElement;
    return { w: wrap?.clientWidth ?? 0, h: wrap?.clientHeight ?? 0 };
  }

  // ------------------------------------------------------------- gestos
  private bindGestures(): void {
    const c = this.canvas;
    c.addEventListener("pointerdown", (e) => {
      c.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.dragging = true;
      this.moved = 0;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      if (this.pointers.size === 2) this.pinchDist = this.twoFingerDist();
    });
    c.addEventListener("pointermove", (e) => {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size >= 2) {
        const d = this.twoFingerDist();
        if (this.pinchDist > 0 && d > 0) {
          const f = d / this.pinchDist;
          if (Math.abs(f - 1) > 0.01) {
            this.scale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * f));
            this.pinchDist = d;
            this.clampCam();
            this.draw();
          }
        }
        return;
      }
      if (!this.dragging) return;
      const dx = e.clientX - this.lastX;
      const dy = e.clientY - this.lastY;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      this.moved += Math.abs(dx) + Math.abs(dy);
      this.offX -= dx / this.scale;
      this.offZ -= dy / this.scale;
      this.clampCam();
      this.draw();
    });
    const end = (e: PointerEvent): void => {
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinchDist = 0;
      if (this.dragging && this.moved < 10) this.tapAt(e.clientX, e.clientY);
      if (this.pointers.size === 0) this.dragging = false;
    };
    c.addEventListener("pointerup", end);
    c.addEventListener("pointercancel", end);
    c.addEventListener("wheel", (e) => {
      e.preventDefault();
      this.zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15);
    }, { passive: false });
  }
  private twoFingerDist(): number {
    const p = [...this.pointers.values()];
    if (p.length < 2) return 0;
    return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
  }
  private tapAt(clientX: number, clientY: number): void {
    const m = this.map;
    if (!m) return;
    const r = this.canvas.getBoundingClientRect();
    const wx = this.offX + (clientX - r.left - r.width / 2) / this.scale;
    const wz = this.offZ + (clientY - r.top - r.height / 2) / this.scale;
    const hit = m.territories.find((t) => Math.abs(wx - t.cx) <= t.halfW && Math.abs(wz - t.cz) <= t.halfH);
    this.selected = hit ? hit.id : null;
    this.renderInfo(); // 10.9L5 — painel ANTES: a altura dele muda o tamanho do mapa (antes sobrava faixa vazia)
    this.draw();
  }

  // ------------------------------------------------------------ desenho
  private renderAll(): void {
    this.titleEl.textContent = this.map ? this.map.regionName : i18n.t("world.title");
    // 10.9L5 — o 👑 sozinho não dizia o que era: agora "👑 Bosses"
    const bb = this.root.querySelector<HTMLElement>(".map-bosses-btn");
    if (bb) bb.innerHTML = `👑<span class="mbb-l">${i18n.t("map.bosses")}</span>`;
    this.renderTabs();
    this.renderGoals(); // 10.9HP
    this.renderInfo(); // 10.9L5 — painel ANTES: a altura dele muda o tamanho do mapa (antes sobrava faixa vazia)
    this.draw();
  }

  /** Abas de REGIÃO no topo (viagem rápida) — substitui a lista de cartões. */
  /** 10.9HP — faixa "Missões da área": o que falta pra abrir o Portal Rift desta região. */
  private goalLabel(kind: string): string {
    const m = this.map;
    return kind === "boss" && m?.goalBoss ? i18n.t(`area.goal.boss.${m.goalBoss}`) : i18n.t(`area.goal.${kind}`); // 10.9L3 — diz QUAL boss
  }

  /**
   * 10.9L4 — ONDE fazer cada missão: os territórios que contam pra ela agora, a mensagem do
   * painel e o mais perto do Caçador. É o que o mapa acende quando a missão está em foco.
   */
  private focusInfo(kind: string): { ids: Set<string>; msg: string; nearest: MapTerritory | null; dist: number } {
    const m = this.map!;
    const ts = m.territories;
    const at = (x: number, z: number): MapTerritory | undefined => ts.find((t) => Math.abs(x - t.cx) <= t.halfW && Math.abs(z - t.cz) <= t.halfH);
    const pend = (t: MapTerritory, k: string): boolean => !!t.tasks?.some((x) => x.kind === k && x.progress < x.target);
    let list: MapTerritory[] = [];
    let msg = "";
    if (kind === "territories") {
      // 10.9L6 — a missão é EXPLORAR (entrar 1× em cada), não conquistar: acende os que faltam visitar
      // nesta volta — de preferência os que já dá pra entrar (portas abertas)
      const falta = ts.filter((t) => !t.visited);
      const abertos = falta.filter((t) => t.open);
      list = abertos.length ? abertos : falta;
      const gl = m.goals?.find((x) => x.kind === "territories");
      const need = gl ? Math.max(0, gl.target - gl.progress) : falta.length; // quantos FECHAM a missão (não o total de ???)
      msg = i18n.t("map.focus.territories", { n: Math.min(need, falta.length) });
    } else if (kind === "champions") {
      list = ts.filter((t) => pend(t, "champion"));
      msg = i18n.t("map.focus.champions", { n: list.length });
    } else if (kind === "chests") {
      list = ts.filter((t) => pend(t, "chest"));
      msg = i18n.t("map.focus.chests", { n: list.length });
    } else if (kind === "kills") {
      list = ts.filter((t) => t.open);
      msg = i18n.t("map.focus.kills");
    } else if (kind === "boss") {
      const b = m.bossTarget;
      const t = b ? at(b.x, b.z) : undefined;
      if (t) list = [t];
      msg = !b ? i18n.t("map.focus.bossUnknown")
        : b.lock ? `🔒 ${b.lock}` // 10.9L5 — Covil/portão ainda fechado: diz o que falta
        : b.ready ? i18n.t("map.focus.boss", { name: b.name })
        : i18n.t("map.focus.bossSleep", { have: Math.min(b.have ?? 0, b.need ?? 0), need: b.need ?? 0 });
    }
    let nearest: MapTerritory | null = null;
    let dist = Infinity;
    for (const t of list) {
      const d = Math.hypot(t.cx - m.player.x, t.cz - m.player.z);
      if (d < dist) { dist = d; nearest = t; }
    }
    if (!list.length && kind !== "boss") msg = i18n.t("map.focus.none");
    return { ids: new Set(list.map((t) => t.id)), msg, nearest, dist };
  }

  private renderGoals(): void {
    const el = this.root.querySelector<HTMLElement>(".map-goals");
    const m = this.map;
    if (!el || !m || !m.goals || !m.goals.length) { if (el) el.innerHTML = ""; return; }
    const done = m.goals.every((g) => g.progress >= g.target);
    // 10.9L4 — cada missão é uma BARRINHA tocável: tocar acende no mapa onde fazer ela
    const chips = m.goals.map((g) => {
      const ok = g.progress >= g.target;
      const pct = Math.round(Math.min(1, g.progress / Math.max(1, g.target)) * 100);
      return `<button class="mg-chip${ok ? " done" : ""}${this.focus === g.kind ? " on" : ""}" data-goal="${g.kind}" aria-pressed="${this.focus === g.kind}">
        <span class="mg-ic">${ok ? "✅" : GOAL_ICON[g.kind] ?? "•"}</span><span class="mg-lbl">${this.goalLabel(g.kind)}</span>
        <span class="mg-bar"><i style="width:${pct}%"></i></span><b>${Math.min(g.progress, g.target)}/${g.target}</b></button>`;
    }).join("");
    // PRÓXIMO PASSO: a missão que falta cujo alvo está mais perto do Caçador
    let next = "";
    if (!done) {
      let best: { kind: string; t: MapTerritory | null; dist: number } | null = null;
      for (const g of m.goals) {
        if (g.progress >= g.target) continue;
        const f = this.focusInfo(g.kind);
        // abater conta em qualquer lugar: só vira o "próximo passo" quando não sobra nada com LUGAR certo.
        // 10.9L5 — boss que ainda não dá pra enfrentar (Covil fechado / Guardião dormindo) vem por último
        const bossBlocked = g.kind === "boss" && !!m.bossTarget && !m.bossTarget.ready;
        const d = g.kind === "kills" ? 1e9 : bossBlocked ? 5e8 : f.nearest ? f.dist : 1e8;
        if (!best || d < best.dist) best = { kind: g.kind, t: f.nearest, dist: d };
      }
      if (best) {
        const bt = m.bossTarget;
        const onde = best.kind === "kills" ? i18n.t("map.anywhere")
          : best.kind === "boss" && bt && !bt.ready ? (bt.lock ?? i18n.t("map.focus.bossSleep", { have: Math.min(bt.have ?? 0, bt.need ?? 0), need: bt.need ?? 0 }))
          : best.t ? `${best.t.discovered ? best.t.name : "???"} (~${Math.round(best.dist)} m)` : "";
        next = `<button class="mg-next" data-goal="${best.kind}">👉 <b>${i18n.t("map.nextStep")}</b> ${this.goalLabel(best.kind)}${onde ? ` — ${onde}` : ""}</button>`;
      }
    }
    const tail = done
      ? `<span class="mg-open">✅ ${i18n.t("world.goalsDone")}</span>`
      : m.nextRegion ? `<span class="mg-open">→ ${i18n.t("world.goalsOpen", { name: m.nextRegion.name, n: String(m.nextRegion.level) })}</span>`
      : m.nextDepth ? `<span class="mg-open">→ ${i18n.t("world.goalsDepth", { n: String(m.nextDepth) })}</span>` : "";
    // 10.9L5 — re-desenhar a faixa não pode jogar a fileira de volta pro começo (tocar na última missão
    // fazia ela sumir da vista); e o fade na borda avisa que tem mais missão escondida
    const sl = el.querySelector<HTMLElement>(".mg-chips")?.scrollLeft ?? 0;
    el.innerHTML = `<div class="mg-head"><span class="mg-title">${i18n.t("world.goals")}</span>${tail}</div><div class="mg-chips">${chips}</div>${next}`;
    const row = el.querySelector<HTMLElement>(".mg-chips");
    if (row) {
      const fade = (): void => {
        const max = row.scrollWidth - row.clientWidth;
        row.classList.toggle("fade-r", max > 2 && row.scrollLeft < max - 2);
        row.classList.toggle("fade-l", max > 2 && row.scrollLeft > 2);
      };
      row.scrollLeft = sl;
      const on = row.querySelector<HTMLElement>(".mg-chip.on");
      if (on && (on.offsetLeft < row.scrollLeft || on.offsetLeft + on.offsetWidth > row.scrollLeft + row.clientWidth)) {
        row.scrollLeft = Math.max(0, on.offsetLeft - (row.clientWidth - on.offsetWidth) / 2); // a missão em foco fica à vista
      }
      row.addEventListener("scroll", fade, { passive: true });
      fade();
      requestAnimationFrame(fade); // 1º abrir: a largura só existe depois do layout
    }
    el.querySelectorAll<HTMLButtonElement>("[data-goal]").forEach((b) => {
      b.onclick = () => this.setFocus(this.focus === b.dataset.goal && !b.classList.contains("mg-next") ? null : b.dataset.goal!);
    });
  }

  /** 10.9L4 — liga/desliga o foco numa missão e leva a câmera até o alvo mais perto. */
  private setFocus(kind: string | null): void {
    this.focus = kind;
    this.selected = null;
    if (kind && this.map) {
      const f = this.focusInfo(kind);
      if (f.nearest) {
        this.offX = f.nearest.cx;
        this.offZ = f.nearest.cz;
        this.scale = Math.max(this.scale, this.minScale * 1.6);
        this.clampCam();
      }
    }
    this.renderGoals();
    this.renderInfo(); // 10.9L5 — painel ANTES: a altura dele muda o tamanho do mapa (antes sobrava faixa vazia)
    this.draw();
  }

  private renderTabs(): void {
    if (!this.provider) return;
    const rows = this.provider();
    this.tabsEl.innerHTML = rows
      .map((r) => {
        const cls = r.current ? "cur" : r.unlocked ? "" : "locked";
        // 10.9HP — aba trancada diz o que falta: os objetivos da área ANTERIOR (compactos) e/ou o nível
        const idx = rows.indexOf(r);
        const prev = idx > 0 ? rows[idx - 1] : null;
        // 10.9L5 — uma frase só ("🔒 Nv 12+ · missões da Floresta 1/5") em vez da fileira de ícones
        const goalsShort = prev?.goals
          ? i18n.t("map.tab.lockGoals", { area: prev.name, done: prev.goals.filter((g) => g.progress >= g.target).length, total: prev.goals.length })
          : i18n.t("world.needGoals");
        const sub = r.current ? i18n.t("terr.here") : r.unlocked ? i18n.t("world.travel") : r.levelOk ? goalsShort : `${i18n.t("world.recLevel", { n: r.recommendedLevel })} · ${goalsShort}`;
        return `<button class="map-tab ${cls}" data-r="${r.id}" ${r.unlocked ? "" : "disabled"}>
          <span class="mt-n">${r.name}</span><span class="mt-s">${r.unlocked ? "" : "🔒 "}${sub}</span>
        </button>`;
      })
      .join("");
    this.tabsEl.querySelectorAll<HTMLButtonElement>(".map-tab[data-r]").forEach((b) => {
      b.onclick = () => {
        const row = rows.find((x) => x.id === b.dataset.r);
        if (!row || row.current || !row.unlocked) return;
        this.handlers.onTravel(b.dataset.r!);
      };
    });
    // 10.9CG — com boss ativo, o mapa é a saída de emergência: fugir = perde a luta, volta ao Santuário
    if (this.map?.bossActive && this.handlers.onFlee) {
      const flee = document.createElement("button");
      flee.className = "map-tab flee";
      flee.innerHTML = `<span class="mt-n">${i18n.t("world.flee")}</span><span class="mt-s">${i18n.t("world.fleeSub")}</span>`;
      flee.onclick = () => this.handlers.onFlee?.();
      this.tabsEl.appendChild(flee);
    }
  }

  private draw(): void {
    const m = this.map;
    const { w, h } = this.size();
    if (!m || w === 0 || h === 0) return;
    // 10.9L9 — o mapa mudou de tamanho desde o enquadramento (painel de baixo cresceu/encolheu):
    // sem zoom do jogador, reenquadra; e sempre reaplica a trava (a grade fica encostada no alto)
    if ((w !== this.fitW || h !== this.fitH) && this.scale <= this.minScale * 1.001) this.fit();
    this.clampCam();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) {
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const k = this.scale;
    const sx = (x: number): number => w / 2 + (x - this.offX) * k;
    const sz = (z: number): number => h / 2 + (z - this.offZ) * k;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#080b14";
    ctx.fillRect(0, 0, w, h);

    // 10.7I — TRILHAS entre territórios (substitui a grade de fundo quadriculada).
    // Linhas ligando o centro de cada território descoberto aos vizinhos descobertos.
    {
      const disc = m.territories.filter(t => t.discovered);
      ctx.strokeStyle = "rgba(150,170,220,0.25)";
      ctx.lineWidth = Math.max(2, k * 2.2);
      ctx.lineCap = "round";
      for (let i = 0; i < disc.length; i++) {
        for (let j = i + 1; j < disc.length; j++) {
          const a = disc[i], b = disc[j];
          const adj = (a.col === b.col && Math.abs(a.row - b.row) === 1) || (a.row === b.row && Math.abs(a.col - b.col) === 1);
          if (!adj) continue;
          ctx.beginPath();
          ctx.moveTo(sx(a.cx), sz(a.cz));
          ctx.lineTo(sx(b.cx), sz(b.cz));
          ctx.stroke();
        }
      }
    }

    // 10.9L4 — ??? VIZINHO: território não descoberto encostado num descoberto (dá pra ir) ganha
    // contorno tracejado e dica (nível / "explorar"); os longe continuam só "???"
    const discSet = new Set(m.territories.filter((t) => t.discovered).map((t) => `${t.col},${t.row}`));
    const nearKnown = (t: MapTerritory): boolean => !t.discovered &&
      [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dc, dr]) => discSet.has(`${t.col + dc},${t.row + dr}`));
    const focus = this.focus ? this.focusInfo(this.focus) : null;
    const labels: (() => void)[] = []; // nomes vão por ÚLTIMO (em cima dos ícones), com fundo
    const lvl = m.playerLevel ?? Infinity;

    // territórios — 10.6Q: cantos arredondados + borda orgânica (não mais quadrados duros)
    for (const t of m.territories) {
      const x = sx(t.cx - t.halfW);
      const y = sz(t.cz - t.halfH);
      const tw = t.halfW * 2 * k;
      const th = t.halfH * 2 * k;
      if (x > w || y > h || x + tw < 0 || y + th < 0) continue;
      const col = TIER_COLOR[Math.min(TIER_COLOR.length - 1, t.tier)] ?? "#5ea86a";
      const r = Math.min(tw, th) * 0.14;
      const ix = x + 2, iy = y + 2, iw = tw - 4, ih = th - 4;
      ctx.beginPath();
      ctx.moveTo(ix + r, iy);
      ctx.lineTo(ix + iw - r, iy); ctx.quadraticCurveTo(ix + iw, iy, ix + iw, iy + r);
      ctx.lineTo(ix + iw, iy + ih - r); ctx.quadraticCurveTo(ix + iw, iy + ih, ix + iw - r, iy + ih);
      ctx.lineTo(ix + r, iy + ih); ctx.quadraticCurveTo(ix, iy + ih, ix, iy + ih - r);
      ctx.lineTo(ix, iy + r); ctx.quadraticCurveTo(ix, iy, ix + r, iy);
      ctx.closePath();
      // 10.9HL — o preenchimento é o PISO do bioma (relva, rocha, gelo, cinza…); a borda segue o tier
      const vizinho = nearKnown(t); // 10.9L4
      ctx.fillStyle = t.ground ? hexA(t.ground, t.discovered ? (t.current ? 0.62 : 0.42) : vizinho ? 0.2 : 0.1) : hexA(col, t.discovered ? (t.current ? 0.34 : 0.17) : vizinho ? 0.12 : 0.06);
      ctx.fill();
      const emFoco = !!focus && focus.ids.has(t.id);
      if (vizinho && !emFoco && this.selected !== t.id) ctx.setLineDash([6, 4]);
      ctx.strokeStyle = this.selected === t.id ? "#ffffff" : emFoco ? "#ffe26a" : hexA(col, t.discovered ? 0.85 : vizinho ? 0.75 : 0.3);
      ctx.lineWidth = this.selected === t.id ? 3 : emFoco ? 3.5 : vizinho ? 2 : 1.4;
      ctx.stroke();
      ctx.setLineDash([]);
      // 10.9L4 — FOCO numa missão: o resto escurece; o que conta brilha
      if (focus && !emFoco) { ctx.fillStyle = "rgba(4,6,12,0.55)"; ctx.fill(); }
      if (emFoco) { ctx.save(); ctx.shadowColor = "#ffe26a"; ctx.shadowBlur = 14; ctx.strokeStyle = "rgba(255,226,106,0.55)"; ctx.lineWidth = 2; ctx.stroke(); ctx.restore(); }
      // nome só quando cabe — 10.9L4: desenhado DEPOIS dos ícones, com fundo (antes sumia atrás deles)
      const fs = Math.max(9, Math.min(15, tw * 0.085));
      if (tw > 62) {
        const label = t.discovered ? t.name : "???";
        const dica = !t.discovered && vizinho ? ((t.needLevel ?? 1) > lvl ? `🔒 Nv ${t.needLevel}` : `🧭 ${i18n.t("map.explore")}`) : "";
        const dim = !!focus && !emFoco;
        labels.push(() => {
          ctx.font = `700 ${fs}px system-ui, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const words = label.split(" ");
          // 10.9L5 — quebra em 2 linhas SÓ se não couber em uma (antes sempre quebrava → plaquinha alta tapando o cartão)
          const cabe = ctx.measureText(label).width <= tw - 20;
          const lines = words.length > 1 && !cabe ? [words.slice(0, Math.ceil(words.length / 2)).join(" "), words.slice(Math.ceil(words.length / 2)).join(" ")] : [label];
          if (dica) lines.push(dica);
          const lh = fs * 1.15;
          const wMax = Math.min(tw - 8, Math.max(...lines.map((ln) => ctx.measureText(ln).width)) + 12);
          // 10.9L5 — nome no TERÇO DE CIMA do cartão (o meio fica livre pro Caçador, baús e monstros);
          // se a plaquinha for larga a ponto de bater no selo de missão (🎯/🏁/🔒 no canto), desce um pouco
          const bh = lines.length * lh + 6;
          const tagH = t.discovered && t.tasks && tw > 40 ? Math.max(10, Math.min(16, tw * 0.09)) + 8 : 0;
          const bateSelo = tagH > 0 && wMax > tw - 2 * (tagH + 10);
          const top = y + Math.max(6, th * 0.1, bateSelo ? tagH + 4 : 0);
          const cy = Math.min(top + bh / 2, y + th / 2);
          if (t.discovered || dica) { // fundo só onde tem texto que importa — sólido mesmo escurecido
            ctx.fillStyle = dim ? "rgba(6,8,16,0.7)" : "rgba(6,8,16,0.78)";
            ctx.beginPath(); ctx.roundRect(x + tw / 2 - wMax / 2, cy - bh / 2, wMax, bh, 6); ctx.fill();
          }
          lines.forEach((ln, i) => {
            const isDica = !!dica && i === lines.length - 1;
            ctx.fillStyle = dim ? "rgba(200,208,230,0.62)" : isDica ? "#ffe26a" : t.discovered ? "rgba(238,243,255,0.97)" : vizinho ? "rgba(200,210,235,0.85)" : "rgba(160,170,200,0.6)";
            ctx.font = `${isDica ? 800 : 700} ${isDica ? Math.max(8, fs * 0.8) : fs}px system-ui, sans-serif`;
            ctx.fillText(ln, x + tw / 2, cy + (i - (lines.length - 1) / 2) * lh, tw - 8);
          });
          if (t.discovered) {
            ctx.fillStyle = hexA(col, dim ? 0.45 : 0.95);
            ctx.font = `800 ${Math.max(8, fs * 0.72)}px system-ui, sans-serif`;
            ctx.fillText(`T${t.tier}`, x + tw / 2, y + th - fs * 0.9);
          }
        });
      }
      if (t.champion && t.discovered) {
        ctx.fillStyle = "#ff5a8a";
        ctx.beginPath();
        ctx.arc(x + tw - 9, y + 9, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      // 10.9HP — estado da missão no cartão: 🏁 conquistado · 🔒 trancado (nível/vizinho) · 🎯 em andamento
      if (t.discovered && t.tasks && tw > 40) {
        const tag = t.cleared ? "🏁" : t.open ? "🎯" : "🔒";
        ctx.font = `${Math.max(10, Math.min(16, tw * 0.09))}px system-ui, sans-serif`;
        ctx.textAlign = "left"; ctx.textBaseline = "top";
        ctx.fillStyle = "#fff";
        ctx.fillText(tag, x + 8, y + 6);
      }
    }

    // 10.9HL — MUROS: cada território descoberto ganha a borda de pedra (a planta real: muro em volta,
    // porta no meio de cada divisa). Antes só as portas apareciam e o mapa parecia um campo aberto.
    if (m.divides && m.divides.length) {
      // 10.9HN — as divisas são RIOS/LAVA/GELO/ABISMO: linha na cor do tipo (só onde um dos lados é conhecido)
      const known = new Set(m.territories.filter((t) => t.discovered).map((t) => t.id));
      ctx.lineCap = "round";
      for (const dv of m.divides) {
        if (!known.has(dv.a) && !known.has(dv.b)) continue;
        const x0 = sx(dv.x0), y0 = sz(dv.z0), x1 = sx(dv.x1), y1 = sz(dv.z1);
        if (Math.max(x0, x1) < -20 || Math.max(y0, y1) < -20 || Math.min(x0, x1) > w + 20 || Math.min(y0, y1) > h + 20) continue;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1);
        ctx.strokeStyle = "rgba(4, 6, 14, 0.8)"; ctx.lineWidth = Math.max(4, k * 5.2); ctx.stroke();
        ctx.strokeStyle = DIVIDE_MAP_COLOR[dv.type as DivideType] ?? "#8aa"; ctx.lineWidth = Math.max(2.2, k * 3.4); ctx.globalAlpha = 0.85; ctx.stroke(); ctx.globalAlpha = 1;
      }
    } else {
      ctx.lineJoin = "round";
      for (const t of m.territories) {
        if (!t.discovered) continue;
        const x = sx(t.cx - t.halfW), y = sz(t.cz - t.halfH), tw = t.halfW * 2 * k, th = t.halfH * 2 * k;
        if (x > w || y > h || x + tw < 0 || y + th < 0) continue;
        ctx.strokeStyle = "rgba(6, 8, 18, 0.85)"; ctx.lineWidth = Math.max(4, k * 2.6); ctx.strokeRect(x, y, tw, th);
        ctx.strokeStyle = "rgba(150, 160, 190, 0.9)"; ctx.lineWidth = Math.max(1.6, k * 1.1); ctx.strokeRect(x, y, tw, th);
      }
    }
    // 10.9EL — PORTAS no Mapa do Mundo (pra PLANEJAR a rota antes de sair andando). Mesmo codigo de
    // cor do minimapa: VERDE aberta, VERMELHA + ponto trancada. Aparece se algum dos dois
    // territorios ja foi descoberto — nao entrega a planta de regioes nunca vistas.
    let legend: (() => void) | null = null; // 10.9L5 — desenhada por ÚLTIMO (os nomes passavam por cima dela)
    if (m.gates && m.gates.length) {
      const known = new Set(m.territories.filter((t) => t.discovered).map((t) => t.id));
      let shown = 0;
      for (const g of m.gates) {
        if (!known.has(g.a) && !known.has(g.b)) continue;
        const gx = sx(g.x), gz = sz(g.z);
        if (gx < -20 || gz < -20 || gx > w + 20 || gz > h + 20) continue;
        const hp = Math.max(5, g.half * k);
        ctx.lineCap = "round";
        ctx.beginPath();
        if (g.alongX) { ctx.moveTo(gx, gz - hp); ctx.lineTo(gx, gz + hp); } else { ctx.moveTo(gx - hp, gz); ctx.lineTo(gx + hp, gz); }
        ctx.strokeStyle = "rgba(0,0,0,0.8)"; ctx.lineWidth = Math.max(6, k * 3.6); ctx.stroke();
        ctx.strokeStyle = g.open ? "#5ff0a0" : "#ff5a6a"; ctx.lineWidth = Math.max(3.5, k * 2.2); ctx.stroke();
        if (!g.open) dot(ctx, gx, gz, Math.max(2.4, k * 1.3), "#ffe0e4");
        shown++;
      }
      // legenda (so quando ha porta visivel)
      if (shown > 0) legend = () => {
        // 10.9L9 — na faixa de baixo, ao lado dos botões (mesma linha do meio deles); tela estreita: logo acima
        const lx = 12, ly = this.legendAbove(w) ? h - 67 : h - 29;
        ctx.font = "700 11px system-ui, sans-serif"; ctx.textAlign = "left"; ctx.textBaseline = "middle";
        const wLeg = 124 + ctx.measureText(i18n.t("map.gateLocked")).width + 10;
        ctx.fillStyle = "rgba(8,11,20,0.9)"; ctx.beginPath(); ctx.roundRect(lx - 6, ly - 11, wLeg, 22, 8); ctx.fill();
        const seg = (x: number, c: string): void => { ctx.strokeStyle = c; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(x, ly); ctx.lineTo(x + 14, ly); ctx.stroke(); };
        seg(lx, "#5ff0a0"); ctx.fillStyle = "#dfe6ff"; ctx.fillText(i18n.t("map.gateOpen"), lx + 20, ly);
        seg(lx + 104, "#ff5a6a"); dot(ctx, lx + 111, ly, 2.2, "#ffe0e4"); ctx.fillStyle = "#dfe6ff"; ctx.fillText(i18n.t("map.gateLocked"), lx + 124, ly);
      };
    }

    // barreira/base
    ctx.strokeStyle = "rgba(157,123,255,0.95)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 5]);
    ctx.beginPath();
    ctx.arc(sx(m.sanctuary.x), sz(m.sanctuary.z), m.sanctuary.r * k, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // POIs — 10.9L4: com foco numa missão, os monstros ficam discretos e o alvo dela cresce
    // 10.9L5 — REVISÃO VISUAL (Thiago: "veja como os baús estão representados"):
    //  · baú = ícone DESENHADO pequeno (não emoji gigante), AGRUPADO quando fica perto (com número),
    //    e SÓ em território já visitado — antes o mapa entregava a posição exata dos baús nos "???";
    //  · no foco "baús", território não visitado ganha um selo "tem baú aqui" (sem a posição);
    //  · monstros menores; nos "???" quase apagados (é o que está perto de você, não um mapa do tesouro).
    const terrAt = (x: number, z: number): MapTerritory | undefined => m.territories.find((t) => Math.abs(x - t.cx) <= t.halfW && Math.abs(z - t.cz) <= t.halfH);
    const dimDots = !!focus && this.focus !== "kills" && this.focus !== "champions";
    const chestFocus = this.focus === "chests";
    const chestPts: { x: number; y: number }[] = [];
    const hiddenChests = new Map<string, number>();
    for (const p of m.pois) {
      const px = sx(p.x);
      const py = sz(p.z);
      if (px < -20 || py < -20 || px > w + 20 || py > h + 20) continue;
      if (p.kind === "enemy" || p.kind === "elite") {
        const known = terrAt(p.x, p.z)?.discovered ?? true;
        ctx.globalAlpha = dimDots ? 0.2 : known ? 0.85 : 0.3;
        dot(ctx, px, py, p.kind === "elite" ? (this.focus === "champions" ? 5.5 : 3.6) : 2.1, p.kind === "elite" ? "#ff5a8a" : "#ff6b6f");
        ctx.globalAlpha = 1;
        continue;
      }
      if (p.kind === "chest") {
        const t = terrAt(p.x, p.z);
        if (t && !t.discovered) { hiddenChests.set(t.id, (hiddenChests.get(t.id) ?? 0) + 1); continue; }
        chestPts.push({ x: px, y: py });
        continue;
      }
      const glyph = p.kind === "sanctuary" ? "🏠" : p.kind === "altar" ? "✦" : p.kind === "rift" ? "◆" : p.kind === "boss" ? "💀" : "▪";
      const color = p.kind === "sanctuary" ? "#7c4dff" : p.kind === "altar" ? "#4cc3ff" : p.kind === "rift" ? "#62e8ff" : p.kind === "boss" ? "#ff3b6b" : "#ffb020";
      const big = this.focus === "boss" && p.kind === "boss";
      // 10.9L5 — covil/portão do boss ainda FECHADO: caveira apagada + cadeado (antes parecia pronto pra luta)
      const trancado = p.kind === "boss" && !!m.bossTarget?.lock && Math.hypot(p.x - m.bossTarget.x, p.z - m.bossTarget.z) < 4;
      ctx.fillStyle = color;
      ctx.font = `700 ${big ? 24 : p.kind === "boss" ? 20 : 17}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      if (trancado) ctx.globalAlpha = 0.55;
      ctx.fillText(glyph, px, py);
      ctx.globalAlpha = 1;
      if (trancado) { ctx.font = `${big ? 14 : 11}px system-ui, sans-serif`; ctx.fillText("🔒", px + (big ? 12 : 10), py + (big ? 10 : 8)); }
    }
    // baús: agrupa os que ficam a menos de ~16 px na tela (vira 1 ícone com o número)
    const grupos: { x: number; y: number; n: number }[] = [];
    const R = chestFocus ? 20 : 16;
    for (const c of chestPts) {
      const g = grupos.find((q) => Math.hypot(q.x - c.x, q.y - c.y) < R);
      if (g) { g.x = (g.x * g.n + c.x) / (g.n + 1); g.y = (g.y * g.n + c.y) / (g.n + 1); g.n++; } else grupos.push({ x: c.x, y: c.y, n: 1 });
    }
    const cs = Math.max(6, Math.min(chestFocus ? 12 : 9, k * (chestFocus ? 2.4 : 1.8)));
    const drawChests = (): void => {
      for (const g of grupos) {
        drawChest(ctx, g.x, g.y, cs, chestFocus);
        if (g.n > 1) badge(ctx, g.x + cs * 0.95, g.y - cs * 0.75, String(g.n));
      }
    };
    if (!chestFocus) drawChests(); // no foco dos baús eles vão POR CIMA dos nomes (senão um some atrás da plaquinha)
    // foco nos baús: território não visitado com baú → selo no canto (sem entregar onde)
    if (chestFocus) {
      for (const [tid, n] of hiddenChests) {
        const t = m.territories.find((x) => x.id === tid);
        if (!t) continue;
        const bx = sx(t.cx + t.halfW) - 16, by = sz(t.cz - t.halfH) + 16;
        drawChest(ctx, bx, by, 8, true);
        badge(ctx, bx + 9, by - 7, n > 1 ? `${n}?` : "?");
      }
    }
    // 10.9L4 — o BOSS DA MISSÃO no mapa: o Colosso (Terras) não tinha ícone nenhum; o Guardião
    // ainda dormindo aparece com 💤 (acorda com abates na área)
    const bt = m.bossTarget;
    if (bt && !m.pois.some((p) => p.kind === "boss" && Math.hypot(p.x - bt.x, p.z - bt.z) < 4)) {
      const px = sx(bt.x), py = sz(bt.z);
      if (px > -20 && py > -20 && px < w + 20 && py < h + 20) {
        ctx.globalAlpha = bt.ready ? 1 : 0.8;
        ctx.fillStyle = "#ff3b6b";
        ctx.font = `700 ${this.focus === "boss" ? 24 : 18}px system-ui, sans-serif`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(bt.ready ? "💀" : bt.lock ? "💀" : "💤", px, py);
        ctx.globalAlpha = 1;
        if (bt.lock) { ctx.font = "11px system-ui, sans-serif"; ctx.fillText("🔒", px + 10, py + 8); }
      }
    }
    // nomes dos territórios POR CIMA dos ícones (com fundo) — o Caçador vem depois, sempre visível
    for (const draw of labels) draw();
    if (chestFocus) drawChests();

    // player
    const px = sx(m.player.x);
    const py = sz(m.player.z);
    const dx = Math.sin(m.player.facing);
    const dy = Math.cos(m.player.facing);
    ctx.fillStyle = "#ffd54a";
    ctx.beginPath();
    ctx.moveTo(px + dx * 11, py + dy * 11);
    ctx.lineTo(px - dy * 7 - dx * 4, py + dx * 7 - dy * 4);
    ctx.lineTo(px + dy * 7 - dx * 4, py - dx * 7 - dy * 4);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.65)";
    ctx.lineWidth = 1.2;
    ctx.stroke();
    legend?.();
  }

  private renderInfo(): void {
    const m = this.map;
    if (!m) return;
    // 10.9L4 — missão em FOCO (sem território tocado): o que fazer e onde fica o mais perto
    if (this.focus && !this.selected) {
      const f = this.focusInfo(this.focus);
      const perto = f.nearest && this.focus !== "kills"
        ? `<div class="wm-meta">${i18n.t("map.focus.nearest", { name: f.nearest.discovered ? f.nearest.name : "???", m: String(Math.round(f.dist)) })}</div>` : "";
      this.infoEl.innerHTML = `<div class="wm-name">${GOAL_ICON[this.focus] ?? ""} ${this.goalLabel(this.focus)}</div><div class="wm-focus">${f.msg}</div>${perto}
        <button class="wm-clear" data-a="unfocus">✕ ${i18n.t("map.focus.clear")}</button>`;
      this.infoEl.querySelector<HTMLButtonElement>('[data-a="unfocus"]')!.onclick = () => this.setFocus(null);
      return;
    }
    const t = m.territories.find((x) => x.id === this.selected) ?? m.territories.find((x) => x.current);
    if (!t) {
      this.infoEl.innerHTML = `<span class="wm-hint">${i18n.t("map.tapGoal")}</span>`;
      return;
    }
    if (!t.discovered) {
      // 10.9L4 — ??? vizinho de um conhecido: diz de quem é vizinho e o nível que pede
      const viz = m.territories.find((o) => o.discovered && Math.abs(o.col - t.col) + Math.abs(o.row - t.row) === 1);
      const lvl = m.playerLevel ?? Infinity;
      const dica = viz
        ? `${i18n.t("terr.unknownNear", { name: viz.name })} ${(t.needLevel ?? 1) > lvl ? i18n.t("terr.unknownLevel", { n: String(t.needLevel) }) : i18n.t("terr.unknownGo")}`
        : i18n.t("terr.unknown");
      this.infoEl.innerHTML = `<div class="wm-name">???</div><div class="wm-meta">${dica}</div>`;
      return;
    }
    const champ = t.champion ? ` · <b class="wm-champ">${i18n.t("terr.champion")}: ${t.champion}</b>` : "";
    // 10.9HP — MISSÃO do território: o que falta pra abrir as portas dele (ou por que está trancado)
    let mission = "";
    if (t.tasks) {
      const parts = t.tasks.map((k) => {
        const done = k.progress >= k.target;
        const label = k.kind === "hunt" ? i18n.t("terr.mtask.hunt", { p: String(Math.min(k.progress, k.target)), t: String(k.target) }) : i18n.t(`terr.mtask.${k.kind}`);
        return `<span class="wm-task${done ? " done" : ""}">${done ? "✅" : "▢"} ${label}</span>`;
      }).join(" ");
      const lvl = m.playerLevel ?? Infinity;
      const state = t.cleared ? `<b class="wm-ok">🏁 ${i18n.t("terr.conquered")}</b>` : t.open ? `<b>🎯 ${i18n.t("terr.mission")}</b>` : (t.needLevel ?? 1) > lvl ? `<b class="wm-lock">🔒 ${i18n.t("terr.needLevelShort", { n: String(t.needLevel) })}</b>` : `<b class="wm-lock">🔒 ${i18n.t("terr.neighborFirst")}</b>`;
      mission = `<div class="wm-mission">${state} ${parts}</div>`;
    }
    this.infoEl.innerHTML =
      `<div class="wm-name">${t.name}${t.current ? ` <span class="wm-here">(${i18n.t("terr.here")})</span>` : ""}</div>` +
      `<div class="wm-meta">${i18n.t("terr.tier", { n: t.tier })} · ${i18n.t("terr.gang", { name: t.gang.join(", ") })}${champ}</div>` + mission;
  }

  /** 10.9FY — GUIA DE BOSSES: onde fica cada um e o que solta (armas/roupas de boss). */
  private toggleBosses(): void {
    const el = this.root.querySelector<HTMLElement>(".map-bosses")!;
    if (!el.hidden) { el.hidden = true; return; }
    const rows: { icon: string; nameKey: string; whereKey: string }[] = [
      { icon: "\u{1F43A}", nameKey: "bossguide.alpha", whereKey: "bossguide.alpha.where" },
      { icon: "\u{1F5FF}", nameKey: "bossguide.colossus", whereKey: "bossguide.colossus.where" },
      { icon: "\u{1F6E1}\uFE0F", nameKey: "bossguide.guardian", whereKey: "bossguide.guardian.where" },
      { icon: "\u{1F447}", nameKey: "bossguide.rift", whereKey: "bossguide.rift.where" },
      { icon: "\u{1F311}", nameKey: "bossguide.titan", whereKey: "bossguide.titan.where" },
    ];
    const esc = (t: string): string => t.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
    el.innerHTML = `<div class="mb-head"><b>\u{1F451} ${esc(i18n.t("bossguide.title"))}</b><button class="mb-x" data-a="mbx">\u2715</button></div>
      <div class="mb-hint">${esc(i18n.t("bossguide.hint"))}</div>
      <div class="mb-list">${rows.map((r) => `<div class="mb-row"><span class="mb-ic">${r.icon}</span><div class="mb-info"><b>${esc(i18n.t(r.nameKey))}</b><small>${esc(i18n.t(r.whereKey))}</small></div></div>`).join("")}</div>`;
    el.querySelector('[data-a="mbx"]')!.addEventListener("click", () => { el.hidden = true; });
    el.hidden = false;
  }
}

/** 10.9L5 — BAÚ desenhado (tampa, corpo, faixa dourada e fecho) — legível em qualquer tamanho,
 *  sem depender do emoji do aparelho (no celular o 📦 saía enorme e diferente em cada marca). */
function drawChest(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, glow: boolean): void {
  const w = s * 1.7, h = s * 1.25;
  const x0 = x - w / 2, y0 = y - h / 2;
  ctx.save();
  if (glow) { ctx.shadowColor = "rgba(255, 207, 74, 0.9)"; ctx.shadowBlur = 12; }
  ctx.fillStyle = "#9a5a1e"; // corpo
  ctx.beginPath(); ctx.roundRect(x0, y0 + h * 0.38, w, h * 0.62, Math.max(1.5, s * 0.18)); ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#d8963a"; // tampa
  ctx.beginPath(); ctx.roundRect(x0, y0, w, h * 0.46, [Math.max(2, s * 0.35), Math.max(2, s * 0.35), 1, 1]); ctx.fill();
  ctx.fillStyle = "#ffd76a"; // faixa + fecho
  ctx.fillRect(x - w * 0.08, y0, w * 0.16, h);
  ctx.fillStyle = "#3a220c";
  ctx.fillRect(x - w * 0.05, y0 + h * 0.4, w * 0.1, h * 0.14);
  ctx.strokeStyle = "rgba(20, 10, 2, 0.85)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.roundRect(x0, y0, w, h, Math.max(1.5, s * 0.2)); ctx.stroke();
  ctx.restore();
}

/** 10.9L5 — selinho com número (quantos baús agrupados / "?" nos não visitados). */
function badge(ctx: CanvasRenderingContext2D, x: number, y: number, txt: string): void {
  ctx.save();
  ctx.font = "800 9px system-ui, sans-serif";
  const bw = Math.max(13, ctx.measureText(txt).width + 7);
  ctx.fillStyle = "#1a1f36"; ctx.strokeStyle = "#ffd76a"; ctx.lineWidth = 1.2;
  ctx.beginPath(); ctx.roundRect(x - bw / 2, y - 7, bw, 14, 7); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#ffe9a8"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(txt, x, y + 0.5);
  ctx.restore();
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function hexA(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
